import { motion, useInView } from "framer-motion";
import { useMemo, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS, CATEGORIES } from "../data/projects";

function ProjectCard({ project, idx }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: (idx % 6) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="mb-5 md:mb-6 break-inside-avoid group relative overflow-hidden rounded-sm bg-[#1c1c1c] border border-white/5 hover:border-[#FD1843]/40 transition-all duration-500"
      data-testid={`project-card-${project.id}`}
    >
      {/* Image — natural aspect ratio, no crop */}
      <div className="relative w-full overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          className="w-full h-auto block transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
        />
        {/* Teal glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ boxShadow: "inset 0 0 60px rgba(253,24,67,0.18)" }}
        />
        {/* Category badge */}
        <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.28em] text-[#FD1843] uppercase bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
          {project.category}
        </div>
      </div>

      {/* Text block */}
      <div className="p-6 md:p-7 space-y-4">
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.28em] text-white/40 uppercase mb-2">
              {project.brand}
            </div>
            <h3 className="font-display text-2xl md:text-[26px] font-semibold tracking-tight text-white">
              {project.title}
            </h3>
          </div>
        </div>
        <p
          dir="rtl"
          className="font-arabic text-white/60 text-sm md:text-[15px] leading-[1.85] font-medium"
        >
          {project.description}
        </p>

        <a
          href={project.link || "#contact"}
          data-testid={`project-view-${project.id}`}
          className="group/btn inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#FD1843] transition-colors pt-2"
        >
          <span className="border-b border-white/20 group-hover/btn:border-[#FD1843] pb-0.5 transition-colors">
            View Project
          </span>
          <ArrowUpRight size={14} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
        </a>
      </div>
    </motion.article>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState(CATEGORIES[0]);
  const headerRef = useRef(null);
  const inView = useInView(headerRef, { once: true, margin: "-80px" });

  const filtered = useMemo(
    () => PROJECTS.filter((p) => p.category === filter),
    [filter]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-32 md:py-48"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="mb-14"
        >
          <div className="flex items-center mb-6">
            <span className="h-rule" />
            <span className="overline">02 — Selected Work</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] leading-[1.02]">
            Selected Brand Projects
            <span className="mx-3 md:mx-4 text-white/20">|</span>
            <span dir="rtl" className="font-arabic text-[#FD1843] font-bold">مشاريع مختارة</span>
          </h2>
          <p
            dir="rtl"
            className="font-arabic mt-6 text-white/60 text-base md:text-lg font-medium leading-[1.9] max-w-3xl"
          >
            مجموعة منتقاة من حملات وسائل التواصل الاجتماعي، والصور المرئية
            للمنتجات، والتصاميم الإعلانية المصممة لعلامات تجارية وشركات حقيقية.
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-10">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.replaceAll(" ", "-").toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full border text-[12px] tracking-tight transition-all duration-300 ${
                  filter === c
                    ? "bg-[#FD1843] border-[#FD1843] text-[#171717]"
                    : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Masonry columns — cards keep natural aspect ratios */}
        <div className="columns-1 md:columns-3 gap-5 md:gap-6 [column-fill:_balance]">
          {filtered.map((p, i) => (
            <ProjectCard key={p.id} project={p} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
